import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

export default function SubmitTestimonial() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [requestInfo, setRequestInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    quote: "",
    rating: 5
  });

  useEffect(() => {
    if (token) {
      fetchRequestInfo();
    } else {
      setError("Invalid link. Please use the link from your email.");
      setIsLoading(false);
    }
  }, [token]);

  const fetchRequestInfo = async () => {
    try {
      const response = await axios.get(`${API}/testimonial-request/${token}`);
      setRequestInfo(response.data);
      setFormData(prev => ({ ...prev, name: response.data.parent_name }));
    } catch (error) {
      if (error.response?.status === 400) {
        setError("You've already submitted a testimonial. Thank you!");
      } else {
        setError("Invalid or expired link. Please contact us if you need help.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/submit-testimonial`, {
        token,
        name: formData.name,
        quote: formData.quote,
        rating: formData.rating
      });
      setIsSuccess(true);
      toast.success("Thank you for your testimonial!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-burns-dark flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-burns-dark flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-white mb-4">Oops!</h1>
          <p className="text-zinc-400 mb-8">{error}</p>
          <Link to="/">
            <Button className="btn-primary">Go to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-burns-dark flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <CheckCircle2 className="w-20 h-20 text-burns-gold mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-white mb-4">THANK YOU!</h1>
          <p className="text-zinc-400 mb-8">
            Your testimonial has been submitted and is pending review. We truly appreciate you taking the time to share your experience!
          </p>
          <Link to="/">
            <Button className="btn-primary px-8 py-4">
              VISIT OUR WEBSITE
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-burns-dark">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="flex items-center justify-center">
            <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-16 w-auto" />
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-xl mx-auto"
        >
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              SHARE YOUR <span className="text-burns-gold">EXPERIENCE</span>
            </h1>
            <p className="text-zinc-400">
              How was <span className="text-white font-medium">{requestInfo?.participant_name}</span>'s experience at <span className="text-burns-gold">{requestInfo?.camp_name}</span>?
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-zinc-950 border border-white/10 rounded-sm p-6 sm:p-8 space-y-6">
            <div>
              <Label className="text-zinc-400">Your Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-transparent border-white/20 text-white mt-1"
                placeholder="Your name"
                required
              />
            </div>

            <div>
              <Label className="text-zinc-400">Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        star <= formData.rating
                          ? "text-burns-gold fill-burns-gold"
                          : "text-zinc-600"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-zinc-400">Your Experience *</Label>
              <Textarea
                value={formData.quote}
                onChange={(e) => setFormData(prev => ({ ...prev, quote: e.target.value }))}
                className="bg-transparent border-white/20 text-white mt-1 min-h-[150px]"
                placeholder="Tell us about your child's experience at camp. What did they enjoy? How did they improve? Would you recommend Burns Elite Baseball to other families?"
                required
              />
              <p className="text-zinc-500 text-xs mt-2">
                Your testimonial may be displayed on our website with your permission.
              </p>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full py-6 text-lg"
              disabled={isSubmitting || !formData.quote}
            >
              {isSubmitting ? "SUBMITTING..." : "SUBMIT TESTIMONIAL"}
            </Button>
          </form>

          <p className="text-center text-zinc-500 text-sm mt-6">
            Thank you for supporting Burns Elite Baseball!
          </p>
        </motion.div>
      </main>
    </div>
  );
}
